<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { createBackup } from '../lib/backup.js'
import { printReport } from '../lib/pdf.js'
import { useModalLayer } from '../composables/useModalLayer.js'
import { usePolicyStore } from '../stores/policyStore.js'
import AppIcon from './AppIcon.vue'

const emit = defineEmits(['new-policy', 'open-panel'])
const store = usePolicyStore()
const menuOpen = ref(false)
const compactHeader = ref(false)
const actionSheet = ref(null)
const sheetClose = ref(null)
let headerMediaQuery

const mobileLayerActive = computed(() => menuOpen.value && compactHeader.value)
useModalLayer({
  rootRef: actionSheet,
  initialFocusRef: sheetClose,
  onClose: () => { menuOpen.value = false },
  active: mobileLayerActive,
})

const familyName = () => store.selectedPerson === 'all' ? (store.people[0] || '家庭') : store.selectedPerson
const exportBackup = () => {
  menuOpen.value = false
  if (!store.policies.length) return store.addNotice('当前没有可导出的保单。', 'warning')
  const blob = new Blob([JSON.stringify(createBackup(store.policies, store.gapGoals, store.gapAdjustments), null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${familyName()}保单备份_${new Date().toISOString().slice(0, 10)}.json`
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  store.addNotice('完整备份已导出。', 'success')
}
const exportPdf = () => {
  menuOpen.value = false
  if (!store.personPolicies.length) return store.addNotice('当前没有可生成报告的保单。', 'warning')
  try { printReport({ policies: store.personPolicies, goals: store.gapGoals, adjustments: store.gapAdjustments, familyName: familyName() }) }
  catch (error) { store.addNotice(error.message, 'danger') }
}
const openPanel = (panel) => { menuOpen.value = false; emit('open-panel', panel) }
const newPolicy = () => { menuOpen.value = false; emit('new-policy') }
const clearWorkspace = () => { menuOpen.value = false; store.resetWorkspace() }
const toggleMenu = async () => {
  menuOpen.value = !menuOpen.value
  if (!menuOpen.value || compactHeader.value) return
  await nextTick()
  document.querySelector('#header-utility-menu [role="menuitem"]')?.focus()
}
const closeDesktopMenu = ({ restoreFocus = false } = {}) => {
  menuOpen.value = false
  if (restoreFocus) document.querySelector('.desktop-more-action')?.focus()
}
const handleDesktopMenuKeydown = (event) => {
  const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]')]
  const currentIndex = items.indexOf(document.activeElement)
  if (event.key === 'Escape') {
    event.preventDefault()
    closeDesktopMenu({ restoreFocus: true })
    return
  }
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const nextIndex = event.key === 'Home' ? 0
    : event.key === 'End' ? items.length - 1
      : event.key === 'ArrowDown' ? (currentIndex + 1) % items.length
        : (currentIndex - 1 + items.length) % items.length
  items[nextIndex]?.focus()
}
const closePopovers = (event) => {
  if (!compactHeader.value && !event.target.closest('.header-more-wrap')) menuOpen.value = false
}
const updateHeaderMode = (event) => {
  compactHeader.value = event.matches
  menuOpen.value = false
}

onMounted(() => {
  headerMediaQuery = window.matchMedia('(max-width: 900px)')
  compactHeader.value = headerMediaQuery.matches
  headerMediaQuery.addEventListener('change', updateHeaderMode)
  document.addEventListener('click', closePopovers)
})
onBeforeUnmount(() => {
  headerMediaQuery?.removeEventListener('change', updateHeaderMode)
  document.removeEventListener('click', closePopovers)
})
</script>

<template>
  <div class="app-layout original-layout" data-app-content>
    <header class="original-header">
      <div class="original-header-inner">
        <div class="brand-area">
          <div class="original-brand-mark"><AppIcon name="brand" tone="white" :size="25"/></div>
          <div class="brand-copy">
            <span><strong>家庭保单管家</strong><small>家庭保障管理与缴费提醒</small></span>
          </div>
        </div>

        <div class="desktop-header-actions">
          <button class="header-ai-action" type="button" @click="openPanel('ai')"><AppIcon name="ai" tone="white" :size="18"/>AI 智能分析</button>
          <button class="header-add-action" data-primary-action type="button" @click="newPolicy"><AppIcon name="add" tone="white" :size="18"/>新增保单</button>
          <div class="header-more-wrap">
            <button class="header-soft-action desktop-more-action" type="button" aria-haspopup="menu" :aria-expanded="menuOpen" aria-controls="header-utility-menu" @click.stop="toggleMenu"><AppIcon name="more" tone="neutral" :size="18"/>更多操作</button>
            <div v-if="menuOpen && !compactHeader" id="header-utility-menu" class="header-action-popover" role="menu" aria-label="更多操作" @keydown="handleDesktopMenuKeydown">
              <button role="menuitem" tabindex="-1" @click="openPanel('backup')"><AppIcon name="import" tone="primary" :size="17"/>导入与数据备份</button>
              <button role="menuitem" tabindex="-1" @click="exportBackup"><AppIcon name="export" tone="success" :size="17"/>导出完整备份</button>
              <button role="menuitem" tabindex="-1" @click="exportPdf"><AppIcon name="pdf" tone="warning" :size="17"/>导出 PDF</button>
              <button role="menuitem" tabindex="-1" @click="openPanel('settings')"><AppIcon name="settings" tone="primary" :size="17"/>设置</button>
              <div class="header-menu-divider" aria-hidden="true"/>
              <button class="header-menu-danger" role="menuitem" tabindex="-1" @click="clearWorkspace"><AppIcon name="delete" tone="danger" :size="17"/>清空看板</button>
            </div>
          </div>
        </div>

        <div class="mobile-header-actions">
          <button class="mobile-more" type="button" aria-label="更多操作" aria-haspopup="dialog" :aria-expanded="mobileLayerActive" aria-controls="mobile-action-sheet" @click.stop="toggleMenu"><AppIcon name="more" tone="neutral" :size="20"/></button>
          <button class="header-add-action" data-primary-action type="button" @click="newPolicy"><AppIcon name="add" tone="white" :size="18"/>新增</button>
        </div>
      </div>
    </header>
    <main id="main-content" class="original-main"><slot/></main>
    <Teleport to="body">
      <div v-if="mobileLayerActive" class="mobile-sheet-layer">
        <button class="mobile-sheet-backdrop" type="button" aria-label="关闭更多操作" @click="menuOpen=false"/>
        <section id="mobile-action-sheet" ref="actionSheet" class="mobile-action-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-action-title" tabindex="-1" @click.stop>
          <div class="sheet-handle" aria-hidden="true"/>
          <header class="sheet-header"><strong id="mobile-action-title">更多操作</strong><button ref="sheetClose" class="icon-button" type="button" aria-label="关闭更多操作" @click="menuOpen=false"><AppIcon name="close" tone="neutral" :size="18"/></button></header>
          <button @click="openPanel('ai')"><span class="soft-ai"><AppIcon name="ai" tone="ai"/></span>AI 智能分析</button>
          <button @click="openPanel('backup')"><span class="soft-primary"><AppIcon name="import" tone="primary"/></span>导入与数据备份</button>
          <button @click="exportBackup"><span class="soft-success"><AppIcon name="export" tone="success"/></span>导出完整备份</button>
          <button @click="exportPdf"><span class="soft-warning"><AppIcon name="pdf" tone="warning"/></span>导出 PDF</button>
          <button @click="openPanel('settings')"><span class="soft-primary"><AppIcon name="settings" tone="primary"/></span>设置</button>
          <div class="sheet-divider" aria-hidden="true"/>
          <button class="sheet-danger" @click="clearWorkspace"><span class="soft-danger"><AppIcon name="delete" tone="danger"/></span>清空看板</button>
        </section>
      </div>
    </Teleport>
  </div>
</template>
